import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { api, setToken, type AuthUser } from '../lib/api'
import { signOut } from '../lib/auth'
import { validatePassword } from '../lib/password'
import { PasswordChecklist } from './PasswordChecklist'
import { useMessages } from '@/i18n/messages'
import { passwordMessages } from '@/i18n/text/password'
import { forcePasswordChangeMessages } from '@/i18n/text/forcePasswordChange'

/**
 * Blocking screen shown when the signed-in user must change their password
 * (first login with a temporary/seeded password, or an admin reset). The rest of
 * the app is unreachable — the API also enforces this server-side (403
 * PasswordChangeRequired) — until a new password is set.
 */
export function ForcePasswordChange({ user }: { user: AuthUser }) {
  const m = useMessages(forcePasswordChangeMessages)
  const pm = useMessages(passwordMessages)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleLogout() {
    try {
      await api.logout()
    } catch {
      /* ignore */
    }
    await signOut(queryClient)
    navigate({ to: '/login' })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const pwError = validatePassword(newPassword)
    if (pwError) {
      setError(pwError)
      return
    }
    if (newPassword !== confirm) {
      setError(pm.mismatch)
      return
    }
    setLoading(true)
    try {
      const res = await api.changePassword(currentPassword, newPassword)
      // Adopt the fresh token and re-fetch identity; the gate clears once
      // mustChangePassword is false.
      setToken(res.token)
      await queryClient.invalidateQueries({ queryKey: ['me'] })
    } catch (err) {
      setError((err as Error).message || m.changeFailed)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="force-pw-title"
        aria-describedby="force-pw-desc"
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
      >
        <h1 id="force-pw-title" className="text-xl font-bold text-slate-900">{m.title}</h1>
        <p id="force-pw-desc" className="mt-2 text-sm text-slate-500">
          {m.signedInAs} <span className="font-medium">{user.email}</span>. {m.mustChange}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.currentPassword}</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.newPassword}</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
            <PasswordChecklist password={newPassword} confirm={confirm} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.confirmNewPassword}</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium rounded px-4 py-2 transition"
          >
            {loading ? m.saving : m.saveNewPassword}
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="w-full text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
          >
            {m.logoutAndSwitch}
          </button>
        </form>
      </div>
    </div>
  )
}
